import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Shield, ShieldCheck, Search, Plus, X, Download, Loader2, Clock, CheckCircle2,
  XCircle, Car, FileText, AlertTriangle, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import { CoverageSelector } from './CoverageSelector';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

const ST = {
  pending: { label: 'Under Review', color: '#F59E0B', icon: Clock },
  approved: { label: 'Approved', color: '#10B981', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: '#EF4444', icon: XCircle },
  voided: { label: 'Voided', color: '#64748B', icon: XCircle },
};
const stOf = (s) => ST[(s || '').toLowerCase()] || { label: s || 'Unknown', color: '#64748B', icon: Clock };

const StatusPill = ({ status }) => {
  const m = stOf(status);
  return <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${m.color}1f`, color: m.color }} data-testid="cert-status-pill"><m.icon className="w-3 h-3" />{m.label}</span>;
};

const downloadCert = async (warrantyId, serial, setBusy) => {
  setBusy(true);
  try {
    const r = await axios.get(`${API}/api/warranties/${warrantyId}/certificate`);
    const b64 = r.data.certificate_pdf; const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length); for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    const a = document.createElement('a'); a.href = url; a.download = `TC-Certificate-${serial || warrantyId}.pdf`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { toast.error(e.response?.data?.detail || 'Could not download certificate'); }
  finally { setBusy(false); }
};

const CertCard = ({ w }) => {
  const [busy, setBusy] = useState(false);
  return (
    <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5" data-testid={`cert-card-${w.warranty_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="w-10 h-10 rounded-lg bg-[#C6A15B]/15 text-[#C6A15B] flex items-center justify-center shrink-0"><Shield className="w-5 h-5" /></span>
          <div className="min-w-0">
            <h4 className="text-slate-100 font-semibold truncate">{[w.vehicle_year, w.vehicle_make, w.vehicle_model].filter(Boolean).join(' ') || 'Vehicle'}</h4>
            <p className="text-slate-500 text-xs mt-0.5">Client: {w.client_name}{w.vin_number ? ` · VIN ${w.vin_number}` : ''}</p>
          </div>
        </div>
        <StatusPill status={w.status} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
        <Field label="Warranty #" mono value={w.serial_number} />
        <Field label="Roll" mono value={w.roll_number || '—'} testid={`warranty-roll-${w.warranty_id}`} accent />
        <Field label="Installed" value={fmtDate(w.installation_date)} />
        <Field label="Expiration" testid={`warranty-expiration-${w.warranty_id}`}
          value={w.status === 'approved' ? (w.warranty_end_date ? fmtDate(w.warranty_end_date) : 'Not Set') : 'Pending TC Approval'} />
        <Field label="Coverage" value={w.coverage_summary || w.installation_location || '—'} />
      </div>

      {w.additional_notes && <p className="text-slate-500 text-xs italic mt-3">{w.additional_notes}</p>}
      {w.admin_notes && (
        <div className={`mt-3 rounded-lg px-3 py-2 text-sm border ${w.status === 'rejected' ? 'bg-red-500/10 border-red-500/20 text-red-300' : 'bg-blue-500/10 border-blue-500/20 text-blue-300'}`}>
          <span className="font-medium">{w.status === 'rejected' ? 'Rejection reason: ' : 'Admin notes: '}</span>{w.admin_notes}
        </div>
      )}
      {w.status === 'approved' && w.warranty_end_date && (
        <div className="mt-3 rounded-lg px-3 py-2 text-sm bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">Valid until {fmtDate(w.warranty_end_date)}</div>
      )}

      {w.status === 'approved' && (
        <button onClick={() => downloadCert(w.warranty_id, w.serial_number, setBusy)} disabled={busy} data-testid={`download-certificate-btn-${w.warranty_id}`}
          className="mt-4 inline-flex items-center gap-2 text-sm px-3.5 py-2 rounded-lg bg-[#C6A15B] text-black font-medium disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Certificate
        </button>
      )}
    </div>
  );
};

const Field = ({ label, value, mono, accent, testid }) => (
  <div>
    <div className="text-slate-500 text-xs">{label}</div>
    <div className={`mt-0.5 font-medium ${mono ? 'font-mono' : ''} ${accent ? 'text-[#C6A15B]' : 'text-slate-200'}`} data-testid={testid}>{value}</div>
  </div>
);

const emptyForm = (user) => ({
  serial_number: '', roll_number: '', client_name: user?.full_name || '',
  vehicle_make: '', vehicle_model: '', vehicle_year: new Date().getFullYear(), vin_number: '',
  vehicle_mileage: '', installation_date: new Date().toISOString().split('T')[0], additional_notes: '',
});

export const WarrantyCenter = ({ user }) => {
  const [tab, setTab] = useState('my');
  const [warranties, setWarranties] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);

  // verify serial tab
  const [serialQ, setSerialQ] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const fetchWarranties = async () => {
    try { const r = await axios.get(`${API}/api/warranties`); setWarranties(r.data || []); }
    catch (e) { setWarranties([]); toast.error('Error loading certificates'); }
  };
  useEffect(() => { fetchWarranties(); }, []);

  const visible = useMemo(() => {
    let list = [...(warranties || [])];
    const n = q.trim().toLowerCase();
    if (n) list = list.filter((w) => [w.serial_number, w.client_name, w.vin_number, `${w.vehicle_make} ${w.vehicle_model}`, w.roll_number].filter(Boolean).some((v) => String(v).toLowerCase().includes(n)));
    if (statusFilter !== 'all') list = list.filter((w) => (w.status || '').toLowerCase() === statusFilter);
    return list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [warranties, q, statusFilter]);

  const runVerify = async (e) => {
    e?.preventDefault();
    if (serialQ.trim().length < 3) { toast.error('Enter at least 3 characters'); return; }
    setVerifying(true); setVerifyResult(null);
    try { const r = await axios.get(`${API}/api/warranties/verify-serial/${encodeURIComponent(serialQ.trim())}`); setVerifyResult(r.data); }
    catch (e) { setVerifyResult({ valid: false, message: 'Error verifying serial number' }); }
    finally { setVerifying(false); }
  };

  const TabBtn = ({ id, label, icon: Icon }) => (
    <button onClick={() => setTab(id)} data-testid={`warranty-tab-${id}`}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${tab === id ? 'bg-[#C6A15B]/12 text-[#E7CB85] font-medium' : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]'}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="warranty-center">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Warranty &amp; Certificates</h2>
          <p className="text-slate-400 text-sm mt-1">Issue and manage TC installation certificates.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="create-certificate-btn"
          className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium hover:bg-[#d4b36e] transition-colors">
          <Plus className="w-4 h-4" /> Create Certificate
        </button>
      </div>

      <div className="flex gap-2 border-b border-white/[0.06] pb-3">
        <TabBtn id="my" label="My Certificates" icon={ShieldCheck} />
        <TabBtn id="verify" label="Verify Serial" icon={Search} />
      </div>

      {tab === 'my' && (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by serial, client, VIN, vehicle..." className={`${inputCls} pl-9`} data-testid="warranty-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} sm:w-auto`} data-testid="warranty-status-filter">
              <option value="all">All Statuses</option>
              <option value="pending">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {warranties === null ? <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32" />)}</div>
            : visible.length === 0 ? (
              <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-16 text-center">
                <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-300 font-medium">{(warranties || []).length === 0 ? 'No certificates yet' : 'No certificates match your filters'}</p>
                <p className="text-slate-500 text-sm mt-1 mb-4">{(warranties || []).length === 0 ? 'Create your first installation certificate.' : 'Try adjusting search or status.'}</p>
                {(warranties || []).length === 0 && <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium"><Plus className="w-4 h-4" /> Create Certificate</button>}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{visible.map((w) => <CertCard key={w.warranty_id} w={w} />)}</div>
            )}
        </>
      )}

      {tab === 'verify' && (
        <div className="max-w-xl">
          <form onSubmit={runVerify} className="flex gap-3">
            <input value={serialQ} onChange={(e) => setSerialQ(e.target.value.toUpperCase())} placeholder="Enter warranty / serial number" className={inputCls} data-testid="verify-serial-input" />
            <button type="submit" disabled={verifying} className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium disabled:opacity-60 shrink-0" data-testid="verify-serial-btn">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verify
            </button>
          </form>
          {verifyResult && (
            <div className={`mt-4 rounded-xl border p-5 ${verifyResult.valid === false ? 'bg-red-500/10 border-red-500/20' : 'bg-[#14171B] border-white/[0.08]'}`} data-testid="verify-serial-result">
              {verifyResult.valid === false ? (
                <p className="text-red-300 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{verifyResult.message || 'Not found'}</p>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-emerald-400 font-medium"><CheckCircle2 className="w-4 h-4" /> {verifyResult.message || 'Serial verified'}</div>
                  {verifyResult.warranty && (
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <Field label="Serial" mono value={verifyResult.warranty.serial_number} />
                      <Field label="Status" value={stOf(verifyResult.warranty.status).label} />
                      <Field label="Client" value={verifyResult.warranty.client_name} />
                      <Field label="Vehicle" value={[verifyResult.warranty.vehicle_year, verifyResult.warranty.vehicle_make, verifyResult.warranty.vehicle_model].filter(Boolean).join(' ')} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showForm && <CreateCertificateModal user={user} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); fetchWarranties(); }} />}
    </div>
  );
};

const CreateCertificateModal = ({ user, onClose, onCreated }) => {
  const [form, setForm] = useState(emptyForm(user));
  const [coverageType, setCoverageType] = useState('package');
  const [packageValue, setPackageValue] = useState('');
  const [coverageItems, setCoverageItems] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const rn = (form.roll_number || '').trim();
    if (rn.length < 3) { setAvailability(null); return; }
    const timer = setTimeout(async () => {
      try { const r = await axios.get(`${API}/api/warranties/roll-availability/${encodeURIComponent(rn)}`); setAvailability(r.data); }
      catch (e) { setAvailability(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.roll_number]);

  const submit = async (e) => {
    e.preventDefault();
    if ((form.serial_number || '').trim().length < 3) { toast.error('Enter a valid serial number'); return; }
    if ((form.roll_number || '').trim().length < 3) { toast.error('Enter a roll number'); return; }
    if (coverageType === 'package' && !packageValue) { toast.error('Select an installation package'); return; }
    if (coverageType === 'individual' && coverageItems.length === 0) { toast.error('Add at least one installation area'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/warranties/request`, {
        ...form,
        coverage_type: coverageType,
        installation_location: coverageType === 'package' ? packageValue : null,
        coverage_items: coverageType === 'individual'
          ? coverageItems.map((it) => ({ area_key: it.area_key, side: it.side || null, custom_description: it.custom_description || null, custom_notes: it.custom_notes || null }))
          : null,
      });
      toast.success('Certificate request submitted');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit request'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="create-certificate-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#14171B] border border-white/[0.08] rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08] sticky top-0 bg-[#14171B] z-10">
          <h3 className="text-white font-semibold flex items-center gap-2"><Shield className="w-5 h-5 text-[#C6A15B]" /> Create Installation Certificate</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Hash className="w-3 h-3" /> Identification</div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Serial / Warranty Number *</label><input className={inputCls} value={form.serial_number} onChange={(e) => upd('serial_number', e.target.value.toUpperCase())} data-testid="cert-serial" /></div>
            <div><label className={labelCls}>Roll Number *</label><input className={inputCls} value={form.roll_number} onChange={(e) => upd('roll_number', e.target.value)} data-testid="cert-roll" /></div>
          </div>

          <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1 pt-1"><Car className="w-3 h-3" /> Customer &amp; Vehicle</div>
          <div><label className={labelCls}>Client Name *</label><input className={inputCls} value={form.client_name} onChange={(e) => upd('client_name', e.target.value)} required data-testid="cert-client" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><label className={labelCls}>Make</label><input className={inputCls} value={form.vehicle_make} onChange={(e) => upd('vehicle_make', e.target.value)} data-testid="cert-make" /></div>
            <div><label className={labelCls}>Model</label><input className={inputCls} value={form.vehicle_model} onChange={(e) => upd('vehicle_model', e.target.value)} data-testid="cert-model" /></div>
            <div><label className={labelCls}>Year</label><input type="number" className={inputCls} value={form.vehicle_year} onChange={(e) => upd('vehicle_year', e.target.value)} data-testid="cert-year" /></div>
            <div><label className={labelCls}>Mileage</label><input className={inputCls} value={form.vehicle_mileage} onChange={(e) => upd('vehicle_mileage', e.target.value)} data-testid="cert-mileage" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>VIN</label><input className={inputCls} value={form.vin_number} onChange={(e) => upd('vin_number', e.target.value.toUpperCase())} data-testid="cert-vin" /></div>
            <div><label className={labelCls}>Installation Date</label><input type="date" className={inputCls} value={form.installation_date} onChange={(e) => upd('installation_date', e.target.value)} data-testid="cert-install-date" /></div>
          </div>

          <div className="text-[11px] uppercase tracking-wide text-slate-500 pt-1">Coverage &amp; Roll Capacity</div>
          <CoverageSelector
            coverageType={coverageType} setCoverageType={setCoverageType}
            packageValue={packageValue} setPackageValue={setPackageValue}
            items={coverageItems} setItems={setCoverageItems}
            availability={availability}
          />

          <div><label className={labelCls}>Additional Notes</label><textarea rows={2} className={inputCls} value={form.additional_notes} onChange={(e) => upd('additional_notes', e.target.value)} data-testid="cert-notes" /></div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="cert-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Submit Certificate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarrantyCenter;
